import React, {
  useState,
  useEffect,
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useCallback,
} from "react";

import { Box, Tooltip, Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { Stack } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import JoditEditor from "jodit-react";
import { useTranslation } from "react-i18next";
import { FiPaperclip } from "react-icons/fi";
import ReactJson from "react-json-view";

import { useAppState } from "../../../context/StateContext";
import useActionShipment from "../../../hooks/useActionShipment";
import { Company } from "../../../models/company";
import { EmailTemplate } from "../../../models/email";
import { AttachmentInfo, Mail } from "../../../models/mail";
import { Shipment, ShipmentMailPayload } from "../../../models/shipments";
import ShipmentService from "../../../services/shipment.service";
import { keysToSnake } from "../../../util/convertCamelCase";
import { ShipmentKeys } from "../../../util/queries";
import { Button, Input, Select } from "../../Form";
import AutoSuggest from "../../Form/AutoSuggest";
import Modal from "../../Generic/Modal";
import SkeletonComponent from "../../Generic/Skeleton";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import Section from "../../Shipments/Maintenance/Details/components/Section";
import { SkeletonFiles, SkeletonEditor } from "./skeleton";
import useStyles, { AttachmentWrapper } from "./styles";

export interface SendShipmentEmailDialogHandles {
  open: (shipmentIds: number[]) => void;
  close: () => void;
}

interface SendShipmentEmailDialogProps {}

const SendShipmentEmailDialog: ForwardRefRenderFunction<
  SendShipmentEmailDialogHandles,
  SendShipmentEmailDialogProps
> = (_, ref) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const queryClient = useQueryClient();

  const [showDialog, setShowDialog] = useState<boolean>(false);

  const [shipmentIds, setShipmentIds] = useState<number[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);

  const [templateId, setTemplateId] = useState<number>();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);

  const [hideCC, setHideCC] = useState<boolean>(false);
  const [hideBCC, setHideBCC] = useState<boolean>(false);

  const [companyId, setCompanyId] = useState<number>();
  const [companies, setCompanies] = useState<Company[]>([]);

  const [subject, setSubject] = useState<string>("");

  const [showJson, setJSONDisplay] = useState(false);

  const [selectedJSONInfo, setJSONInfo] = useState("");
  const [selectedFile, setSelectedFile] = useState<AttachmentInfo>();
  const [jsonAttachments, setAttachments] = useState<AttachmentInfo[]>([]);

  const [localEditorState, setEditorState] = useState("");

  const resetStates = useCallback(() => {
    setShipmentIds([]);
    setShipments([]);
    setJSONDisplay(false);
    setJSONInfo("");
    setSelectedFile(undefined);
    setAttachments([]);
    setTemplates([]);
    setTemplateId(undefined);
    setHideCC(false);
    setHideBCC(false);
    setCompanyId(currentUser?.company?.id);
    setCompanies([]);
    setSubject("");
  }, []);

  const openModal = useCallback((fromShipmentIds: number[]) => {
    setShipmentIds(fromShipmentIds);
    setShowDialog(true);
    if (currentUser?.company) {
      setCompanyId(currentUser.company.id);
      setCompanies([currentUser?.company]);
    }
  }, []);

  const closeModal = useCallback(() => {
    setShowDialog(false);
    resetStates();
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const { getExportShipmentsById } = useActionShipment();

  const results = getExportShipmentsById(setShipments, shipmentIds);

  const isLoading = results.some((result) => result.isFetching);
  const isFetched = results.every((result) => result.isFetched);

  useEffect(() => {
    if (isFetched && !isLoading) {
      loadFiles(shipments);

      const containerIds = shipments.map((shipment: Shipment) => shipment.containerId);

      setSubject(`Shipment # ${containerIds.join(",")}`);
    }
  }, [shipments]);

  const {
    data: emailsAvailable = [],
    isFetching: loadingEmailsAvailable,
    refetch: refetchEmailsAvailable,
  } = useQuery({
    queryKey: ["shipment.emails.available", currentUser?.companyId],
    queryFn: async () => {
      const response = await ShipmentService.getAvailableEmails({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["email", "asc"]]),
      });

      if (response && response.data && response.data instanceof Array) return response.data;

      return null;
    },
  });

  const { mutate: sendEmail, isLoading: loadingSendEmail } = useMutation({
    mutationKey: ShipmentKeys.sendMail(shipmentIds),
    mutationFn: (data: ShipmentMailPayload) => ShipmentService.sendMail(data),
    onSuccess: () => {
      NotificationToast.success(translate("emailSentSuccesfuly"));
      queryClient.refetchQueries({ queryKey: [ShipmentKeys.all] });
      refetchEmailsAvailable();
      setEmailSelecteds([]);
      setEmailCCSelecteds([]);
      setEmailBCCSelecteds([]);

      closeModal();
    },
    onError: () => {
      NotificationToast.error(translate("couldNotSendEmail"));
    },
  });

  // Func::: Load file generation based from selected shipment records
  const loadFiles = (shipments: Shipment[]) => {
    if (!currentUser) return;
    if (!shipments) return;

    const shipIds: string[] = [];

    shipments.forEach((shipment: Shipment) => {
      if (shipment.containerId) {
        const attachInfo = generateFile([shipment], shipment.containerId);

        setAttachments((attachments) => [...attachments, attachInfo]);

        shipIds.push(shipment.containerId);
      }
    });

    const htmlMsgBodyTemplate = `<div>
     <p>${translate("hiThere")}</p><span>${translate(
      "pleaseFindTheJsonFileAttachedForShipmentS"
    )} #${shipIds.join(",")} </span><br><p>${translate("best")}</p><p>${
      currentUser.name ? currentUser.name : ""
    } ${currentUser.lastName ? currentUser.lastName : ""}</p></div>`;

    setEditorState(htmlMsgBodyTemplate);
  };

  // Func::: File attachment generator for JSON file
  const generateFile = (rawData: any, itemID: String) => {
    const jsonRawInfo = JSON.stringify(keysToSnake(rawData));

    const attachInfo = {
      content: jsonRawInfo,
      filename: `${itemID}.json`,
      type: "application/json",
      disposition: "attachment",
    };

    return attachInfo;
  };

  // Func::: Extract JSON Info from selected File
  const getJSONforDisplay = (fileInfo: AttachmentInfo) => {
    setSelectedFile(fileInfo);
    setJSONInfo(fileInfo.content);
  };

  // Func::: Send Email
  const handleSendEmail = async () => {
    if (!currentUser) return;

    if (emailSelecteds.length === 0 || jsonAttachments.length === 0) return;

    const mail: Mail = {
      attachments: jsonAttachments,
      isHTML: true,
      fromName: companies.find((company) => company.id === companyId)?.name || "",
      toEmail: emailSelecteds,
      message: `<div style="padding: 10px;">  <a href="https://shipeezi.com" target="_blank">
        <img src="https://shipeezi-fe-img-assets.s3.ap-southeast-2.amazonaws.com/sd.png" alt="Powered by Shipeezi" style="height: 26px" /></a>
        <hr />
        ${localEditorState}
        <br><p>Powered by <a href="https://shipeezi.com" target="_blank">Shipeezi</a></p><hr /><p>Shipeezi, 300 Barangaroo Ave, Sydney NSW 2000, Australia</p></div>`,
      subject,
      bcc: emailBCCSelecteds.join(","),
      cc: emailCCSelecteds.join(","),
    };

    sendEmail({ mail, shipmentIds, companyId: currentUser?.companyId });
  };

  const handleClose = () => {
    closeModal();
  };

  const renderLocalEditorState = () => {
    if (isLoading) {
      return <SkeletonEditor />;
    }

    if (!localEditorState) return;

    return (
      <Box className={classes.bxMessageWrap}>
        <JoditEditor
          ref={null}
          config={{
            readonly: false,
            buttons: "bold,italic,underline,strikethrough,ul,ol,indent,outdent,left",
          }}
          value={localEditorState}
          onBlur={(newContent) => {
            setEditorState(newContent);
          }}
        />
      </Box>
    );
  };

  const renderAttachmentFiles = () => {
    if (isLoading) {
      return <SkeletonFiles />;
    }

    if (!jsonAttachments) return;

    const setJsonToDisplay = (attItem: AttachmentInfo) => {
      if (selectedFile && selectedFile?.filename === attItem.filename) {
        setJSONDisplay(false);
        setSelectedFile(undefined);
      } else {
        getJSONforDisplay(attItem);
        setJSONDisplay(true);
      }
    };

    return jsonAttachments.map((attItem, index) => (
      <Tooltip
        title={translate("clickToViewJson") || ""}
        key={index}
        data-cy={`attachment-${attItem.filename}`}
      >
        <AttachmentWrapper
          icon={<FiPaperclip color="#3F6280" style={{ marginRight: "0px" }} />}
          size="small"
          label={`#${attItem.filename}`}
          className={classes.attachmentBadge}
          onClick={() => {
            setJsonToDisplay(keysToSnake(attItem));
          }}
        />
      </Tooltip>
    ));
  };

  const [emailSelecteds, setEmailSelecteds] = useState<string[]>([]);
  const [emailCCSelecteds, setEmailCCSelecteds] = useState<string[]>([]);
  const [emailBCCSelecteds, setEmailBCCSelecteds] = useState<string[]>([]);

  return (
    <Modal
      open={showDialog}
      handleClose={handleClose}
      title={<div style={{ padding: 8 }}>{translate("sendEmail")}</div>}
      showActionButtons
      actionButtonProps={{
        loading: loadingSendEmail,
        onCancel: handleClose,
        onSave: handleSendEmail,
        textSave: translate("send"),
        saveDisabled: emailSelecteds.length === 0 || jsonAttachments.length === 0,
      }}
    >
      <Section name={translate("emailDetails")} style={{ margin: 0 }}>
        <Grid alignItems="flex-end" container spacing={1} style={{ padding: 8 }}>
          <Grid item md={6}>
            <SkeletonComponent isLoading={loadingEmailsAvailable}>
              <Select
                disabled={templates.length === 0}
                displayEmpty
                label={translate("template")}
                onChange={(event: SelectChangeEvent<any>) => setTemplateId(event.target.value)}
                value={templateId}
              >
                {templates.map((template) => (
                  <MenuItem key={`template-${template.id}`} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent>
          </Grid>
          <Grid item md={6}>
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setHideCC((state: boolean) => !state)}>
                {translate(`${hideCC ? "showCC" : "hideCC"}`)}
              </Button>
              <Button onClick={() => setHideBCC((state: boolean) => !state)}>
                {translate(`${hideBCC ? "showBCC" : "hideBCC"}`)}
              </Button>
            </Stack>
          </Grid>
          <Grid item md={6}>
            <SkeletonComponent isLoading={loadingEmailsAvailable}>
              <AutoSuggest
                disabled={loadingSendEmail}
                freeSolo
                getOptionLabel={(option: string) => option}
                label={translate("to")}
                multiple
                onChange={(emails: string[]) => setEmailSelecteds(emails)}
                options={(emailsAvailable || []).map((emailData) => emailData.email)}
                placeholder={translate("typeTheEmailAddressEnter")}
                removeShadowBox={false}
                required
                value={emailSelecteds}
              />
            </SkeletonComponent>
          </Grid>
          <Grid item md={6}>
            <SkeletonComponent isLoading={loadingEmailsAvailable}>
              <Select
                label={translate("from")}
                onChange={(event: SelectChangeEvent<any>) => setCompanyId(event.target.value)}
                value={companyId}
              >
                {companies.map((company) => (
                  <MenuItem key={`company-${company.id}`} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent>
          </Grid>
          <Grid item md={6}>
            {!hideCC && (
              <SkeletonComponent isLoading={loadingEmailsAvailable}>
                <AutoSuggest
                  disabled={loadingSendEmail}
                  freeSolo
                  getOptionLabel={(option: string) => option}
                  label={translate("cc")}
                  multiple
                  onChange={(emailsCC: string[]) => setEmailCCSelecteds(emailsCC)}
                  options={(emailsAvailable || []).map((emailData) => emailData.email)}
                  placeholder={translate("typeTheEmailAddressEnter")}
                  removeShadowBox={false}
                  value={emailCCSelecteds}
                />
              </SkeletonComponent>
            )}
          </Grid>
          <Grid item md={6}>
            {!hideBCC && (
              <SkeletonComponent isLoading={loadingEmailsAvailable}>
                <AutoSuggest
                  disabled={loadingSendEmail}
                  freeSolo
                  getOptionLabel={(option: string) => option}
                  label={translate("bcc")}
                  multiple
                  onChange={(emailsBCC: string[]) => setEmailBCCSelecteds(emailsBCC)}
                  options={(emailsAvailable || []).map((emailData) => emailData.email)}
                  placeholder={translate("typeTheEmailAddressEnter")}
                  removeShadowBox={false}
                  value={emailBCCSelecteds}
                />
              </SkeletonComponent>
            )}
          </Grid>
          <Grid item md={12}>
            <SkeletonComponent isLoading={loadingEmailsAvailable}>
              <Input
                value={subject}
                label={translate("subject")}
                onChange={(event) => setSubject(event.target.value || "")}
                placeholder={translate("subject")}
              />
            </SkeletonComponent>
          </Grid>
        </Grid>
      </Section>
      <Section name={translate("emailContent")} style={{ margin: 0, marginTop: 24 }}>
        <Grid container spacing={1} style={{ padding: 8 }}>
          <Grid item md={12}>
            {renderLocalEditorState()}
          </Grid>
          <Grid item md={12}>
            <Box>{renderAttachmentFiles()}</Box>
          </Grid>
          {showJson && (
            <Grid className={classes.jsonBox} item md={12}>
              <ReactJson
                collapsed
                indentWidth={2}
                src={JSON.parse(selectedJSONInfo)}
                style={{ height: "100%", maxHeight: "100%", overflowY: "auto" }}
              />
            </Grid>
          )}
        </Grid>
      </Section>
    </Modal>
  );
};

export default forwardRef(SendShipmentEmailDialog);
